import { useMemo, useState } from "react";
import { Button, Loading, Modal } from "@/components/atoms";
import { DataTable, NoSearchResults, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePagination } from "@/hooks";
import { driverApi, useToggleFleetLeaderMutation, useUpdateFleetStatusMutation } from "@/redux/apis/driver/driverApi";
import { useAppDispatch } from "@/redux/hooks";
import { FleetDriver } from "@/redux/slices/driver/types";
import { Page } from "@/redux/types";
import { addToast } from "@/utils";
import { MyDriversEmptyState } from "./components/MyDriversEmptyState";
import { generateMyDriversTableColumns, generateMyDriversTableData } from "./helpers";
import { useGetFleetDriversParams } from "./hooks/useGetFleetDriversParams";

interface MyDriversTableProps {
  items: FleetDriver[];
  page?: Page;
}

export const MyDriversTable = ({ items }: MyDriversTableProps) => {
  const dispatch = useAppDispatch();
  const { sorting, setSorting, search, filters } = useGetFleetDriversParams();
  const [toggle, { isLoading: isLoadingToggle }] = useToggleFleetLeaderMutation();
  const [updateFleetStatus, { isLoading: isLoadingUpdateStatus }] = useUpdateFleetStatusMutation();
  const isFiltersEmpty = Object.values(filters).every((el) => el === undefined);
  const isLoading = isLoadingToggle || isLoadingUpdateStatus;

  const [unsuspendDriverId, setUnsuspendDriverId] = useState<string | null>(null);

  const confirmUnsuspendDriver = () => {
    if (!unsuspendDriverId) return;
    updateFleetStatus({ id: unsuspendDriverId, account_status: "active" })
      .unwrap()
      .then(() => {
        dispatch(driverApi.util.invalidateTags(["FleetDrivers"]));
        addToast("success", "Successfully unsuspended driver.");
        setUnsuspendDriverId(null);
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const toggleFleetLeader = (id: string, isFleetLeader: boolean) => {
    toggle({ id, isFleetLeader })
      .unwrap()
      .then(() => {
        dispatch(driverApi.util.invalidateTags(["FleetDrivers"]));
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const renderEmptyState = () => {
    if (search) return <NoSearchResults />;
    if (!isFiltersEmpty) return <MyDriversEmptyState hasFilters />;

    return <MyDriversEmptyState />;
  };

  const columns = useMemo(() => generateMyDriversTableColumns(), []);
  const data = useMemo(
    () => generateMyDriversTableData(items, setUnsuspendDriverId, toggleFleetLeader, isLoadingToggle),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, isLoading]
  );

  return (
    <div className="relative">
      {isLoading && <Loading className="rounded-lg" />}
      <DataTable
        className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
        data={data}
        columns={columns}
        sorting={sorting}
        setSorting={setSorting}
        emptyState={renderEmptyState()}
      />
      <Modal
        className="relative w-[85vw] md:w-[400px] lg:w-[400px]"
        open={!!unsuspendDriverId}
        onClose={() => (isLoadingUpdateStatus ? null : setUnsuspendDriverId(null))}
        title="Unsuspend Driver"
        description="Unsuspending this driver will restore their access to the driver app. They'll be able to take jobs again once unsuspended."
        variant="web-small"
        renderButtons={() => (
          <>
            <Button variant="secondary" size="md" onClick={() => setUnsuspendDriverId(null)} disabled={isLoadingUpdateStatus}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={confirmUnsuspendDriver} disabled={isLoadingUpdateStatus}>
              Unsuspend Driver
            </Button>
          </>
        )}
      >
        {isLoadingUpdateStatus && <Loading />}
      </Modal>
    </div>
  );
};

const MyDriversTablePagination = ({ page }: { page: Page }) => {
  const { handlePageClick, handleSizeChange } = usePagination("my-drivers");

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={page.current}
      pageSize={page.size}
      totalItems={page.total}
      pageCount={page.count}
    />
  );
};

MyDriversTable.Pagination = MyDriversTablePagination;
